const { Default: _, match, sortBy } = require('@nike/rcf-fp');
const getSymbolFromCurrency = require('currency-symbol-map');
const { cloneDeep } = require('lodash');
const moment = require('moment-timezone');
const numeral = require('numeral');

const { NEGATIVE_AMOUNT_FORMAT_LOCALES } = require('./constants');
const { isNegativeAmount } = require('./ej-utils');
require('numeral/locales');

const TRUNCATED_ACCOUNT_LENGTH = 14;
const TRUNCATED_ACCOUNT_LENGTH_KOREA = 16;
const CURRENCY_FORMAT_EXCEPTION_LOCALES = ['cs-CZ', 'da-DK', 'de-CH', 'en-SG', 'es-AR', 'fr-BE', 'fr-CH', 'he-IL', 'hr-HR', 'it-CH', 'ja-JP', 'nn-NO', 'pl-PL', 'pt-PT', 'sv-SE', 'th-TH', 'tr-TR', 'zh-TW'];
const overrideCurrencySymbols = {
  CHF: 'Fr.',
  DKK: 'Kr.',
  HKD: 'HK$',
  YEN: '¥'
};

const getPromoText = (modifier) => {
  if (modifier?.MethodCode === 'Promotion') {
    return modifier?.Promotion?.Code || '';
  } else if (modifier?.MethodCode === 'PriceOverride' || modifier?.MethodCode === 'GeneralDiscount') {
    return modifier?.Reason?.Description || '';
  } else if (modifier?.Employee?.ID) {
    return modifier?.Descriptions?.find((i) => i.TypeCode === 'Long')?.Text || modifier?.Descriptions?.find((i) => i.TypeCode === 'Short')?.Text || '';
  } else {
    return (`${modifier?.Descriptions?.find((i) => i.TypeCode === 'Short')?.Text}`) || '';
  }
};

const dateFormatter = (date) => `${moment.utc(date).format('YYYY-MM-DD HH:mm')}:00`;

const dateFormatterTable = (date, timezone, format) => moment.utc(date).tz(timezone).format(`${format} hh:mm A`);

const extractAmountFilters = (minimumAmount, maximumAmount) => (minimumAmount && maximumAmount
  ? [`transactionAmount >= ${minimumAmount}`, `transactionAmount <= ${maximumAmount}`]
  : []);

// filter out falsey keys and convert the first character to 'UpperCase'
const extractBooleanFilters = (object = {}) => Object.entries(object)
  .filter(([, value]) => value)
  .map(([key]) => `'${key.charAt(0).toUpperCase() + key.substring(1)}'`);

const extractCategoryFilters = (transactionCategory) => (transactionCategory
  ? [`transactionCategory ${transactionCategory === 'RetailTransaction'
    ? `=in= ('OrderTransaction','${transactionCategory}')`
    : `== '${transactionCategory}'`}`]
  : []);

const extractDateFilters = (transactionStartDate, transactionEndDate) => match()(
  [() => !transactionStartDate && transactionEndDate, [`beginDateTime <= '${transactionEndDate}'`]],
  [() => transactionStartDate && !transactionEndDate, [`beginDateTime >= '${transactionStartDate}'`]],
  [() => transactionStartDate !== transactionEndDate, [`beginDateTime >= '${transactionStartDate}'`, `beginDateTime <= '${transactionEndDate}'`]],
  [_, []],
);

const extractModifierFilters = (modifiers) => ((modifiers && Array.isArray(modifiers) && modifiers.length)
  ? [`transactionModifiers ${modifiers.length > 1 ? `=in= (${modifiers})` : `== ${modifiers}`}`]
  : []);

// pattern is (key == 'value'), except for three, where it is (key == value), without single quotes
const NO_QUOTE_FILTERS = ['transactionAmount', 'transactionNumber'];
const extractSearchFilters = (searchFilters = {}) => Object.entries(searchFilters)
  .filter(([, value]) => !!value)
  .map(([key, value]) => (NO_QUOTE_FILTERS.includes(key)
    ? `${key} == ${value}`
    : `${key} == '${value}'`));

const formatFreeText = (freeText) => (freeText ? freeText.toLowerCase().replace(/ /g, '=space=').replace(/'/g, '=apos=') : '');

const extractFreeTextFilter = (freeTextFilter) => (freeTextFilter ? [`freeText =like= '%25${freeTextFilter}%25'`] : []);

const extractReturnTransactionFilter = (hasReturn) => (hasReturn === 'Return' ? [`transactionHasReturn == '${hasReturn}'`] : []);

const extractTrainingModeFilter = (mode) => match(mode)(
  ['training', ['trainingMode == true']],
  ['nonTraining', ['trainingMode == false']],
  [_, []]
);

const extractStatusFilters = (status) => ((status && Array.isArray(status) && status.length)
  ? [`transactionStatus ${status.length > 1 ? `=in= (${status})` : `== ${status}`}`]
  : []);

const extractTenderTypeAndSubFilters = (tenderTypes, cardNumber, subTenderType) => {
  const tenderType = ['Credit', 'Debit'].includes(tenderTypes)
    ? `== 'CreditDebit`
    : `== '${tenderTypes}`;

    return match(tenderTypes, cardNumber, subTenderType)(
    [() => !tenderTypes, []],
    [() => cardNumber, [`tenderWithCardNumber ${tenderType}-${cardNumber}'`]],
    [() => subTenderType && !['', 'All'].includes(subTenderType), [`tenderWithSubTender ${tenderType}-${subTenderType}'`]],
    [_, [`tenderTypes ${tenderType}'`]]
  );
};

const cardEntryMethod = (cardInfo) => match(cardInfo?.EntryMethod?.toUpperCase())(
  [(method) => ['INTEGRATEDCHIPCARD'].includes(method), '<D>'],
  [(method) => ['KEYED'].includes(method), '<K>'],
  [(method) => ['RFID', 'TAPPED', 'CONTACTLESS'].includes(method), '<R>'],
  [(method) => ['SWIPED', 'MSR'].includes(method), '<S>'],
  [(method) => ['NONE'].includes(method), '<C>'],
  [_, ''],
);

// return formatted currency string handled exceptionally
const currencyFormatterException = (locale, amount) => match(locale)(
  ['cs-CZ', `${new Intl.NumberFormat(locale).format(amount)} Kč`],
  ['da-DK', `kr. ${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  [() => ['de-CH', 'fr-CH', 'it-CH'].includes(locale), `Fr. ${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['en-SG', `S$${numeral(amount).format('0,0.00')}`],
  ['es-AR', `ARS ${numeral(amount).format('0,0.00')}`],
  ['fr-BE', `${new Intl.NumberFormat('nl-BE', { currency: 'EUR', style: 'currency' }).format(amount)}`],
  ['he-IL', `₪ ${numeral(amount).format('0,0.00')}`],
  ['hr-HR', `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)} kn`],
  ['ja-JP', `¥${numeral(amount).format('0,0')}`],
  ['nn-NO', `kr ${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['pl-PL', `${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount).replace('.', '')} zl`],
  ['pt-PT', `${new Intl.NumberFormat('el-GR', { currency: 'EUR', style: 'currency' }).format(amount)}`],
  ['sv-SE', `${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)} kr`],
  ['th-TH', `THB${numeral(amount).format('0,0.00')}`],
  ['tr-TR', `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount).replace('.', '')}`],
  ['zh-TW', `NT$${numeral(amount).format('0,0.00')}`],
  [_, '']
);

// return formatted currency string without the currency symbol handled exceptionally
const currencyNumberFormatterException = (locale, amount) => match(locale)(
  ['cs-CZ', `${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['da-DK', `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['de-CH', `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['en-SG', `${numeral(amount).format('0,0.00')}`],
  ['es-AR', `${numeral(amount).format('0,0.00')}`],
  ['fr-BE', `${new Intl.NumberFormat('nl-BE').format(amount)}`],
  ['hr-HR', `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['ja-JP', `${numeral(amount).format('0,0')}`],
  ['nn-NO', `${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['pl-PL', `${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount).replace('.', '')}`],
  ['pt-PT', `${new Intl.NumberFormat('el-GR').format(amount)}`],
  ['sv-SE', `${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount)}`],
  ['th-TH', `${numeral(amount).format('0,0.00')}`],
  ['tr-TR', `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount).replace('.', '')}`],
  ['zh-TW', `${numeral(amount).format('0,0.00')}`],
  [_, '']
);

const discountFormatter = (amount, storeLocale) => match(amount, storeLocale)(
  [() => NEGATIVE_AMOUNT_FORMAT_LOCALES.includes(storeLocale), `-${amount}`],
  [_, `(${amount})`],
);

const currencyFormatter = (storeLocale, storeCurrency, amount) => {
  if (CURRENCY_FORMAT_EXCEPTION_LOCALES.includes(storeLocale)) {
    return currencyFormatterException(storeLocale, amount);
  }
  return new Intl.NumberFormat(storeLocale || 'en-US', { currency: storeCurrency || 'USD', style: 'currency' }).format(amount || 0);
};

// return formatted currency string without the currency symbol
const currencyNumberFormatter = (storeLocale, amount) => {
  if (CURRENCY_FORMAT_EXCEPTION_LOCALES.includes(storeLocale)) {
    return currencyNumberFormatterException(storeLocale, amount);
  }
  return new Intl.NumberFormat(storeLocale || 'en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount || 0);
};

// returns filter string format: filter=key1(value1)&filter=key2(value2)...
const formatFilters = (filters = {}) => {
  const filterArray = Object.entries(filters);
  return filterArray.length
    ? [filterArray.map(([key, value]) => `filter=${key}(${value})`).join('&')]
    : [];
};

const searchDateFilter = (start, end, duration) => {
  const startDate = moment(start);
  const endDate = moment(end);
  const between = new Set();

  if (startDate < endDate) {
    const date = startDate.startOf(duration);

    // eslint-disable-next-line fp/no-loops
    while (date < endDate.endOf(duration)) {
      between.add(date.format(duration === 'month' ? 'MM' : 'YYYY'));
      date.add(1, duration);
    }
  }
  return [`${duration} =in= (${[...between]})`];
};

const formatSearchQueries = (fields = {}) => {
  const copyFields = cloneDeep(fields);

  const searchMonth = searchDateFilter(copyFields.transactionStartDate, copyFields.transactionEndDate, 'month');
  const searchYear = searchDateFilter(copyFields.transactionStartDate, copyFields.transactionEndDate, 'year');
  const searchFilters = extractSearchFilters(copyFields.searchFilters);
  const freeTextFilter = extractFreeTextFilter(formatFreeText(copyFields.freeText));
  const transactionHasReturn = extractReturnTransactionFilter(copyFields.transactionHasReturn);
  const trainingModeFilter = extractTrainingModeFilter(copyFields.trainingMode);
  const categoryFilters = extractCategoryFilters(copyFields.transactionCategory);
  const dateRangeFilters = extractDateFilters(copyFields.transactionStartDate, copyFields.transactionEndDate);
  const amountFilters = extractAmountFilters(copyFields.minimumAmount, copyFields.maximumAmount);
  const tenderTypeAndCardNumberFilters = extractTenderTypeAndSubFilters(copyFields.tenderTypes, copyFields.cardNumber, copyFields.subTenderType);
  const statusFilters = extractStatusFilters(extractBooleanFilters(copyFields.status));
  const modifierFilters = extractModifierFilters(extractBooleanFilters(copyFields.modifiers));

  const queries = [
    ...searchMonth,
    ...searchYear,
    ...searchFilters,
    ...freeTextFilter,
    ...transactionHasReturn,
    ...categoryFilters,
    ...dateRangeFilters,
    ...amountFilters,
    ...tenderTypeAndCardNumberFilters,
    ...statusFilters,
    ...modifierFilters,
    ...trainingModeFilter,
  ].join(' and ');
  return queries ? [`search=(${queries})`] : [];
};

const sortStoresByNumber = (stores) => sortBy(
  stores,
  (store) => store.full.storeNumber,
  // eslint-disable-next-line no-nested-ternary
  (left, right) => ((/[a-z]/i.test(left) || /[a-z]/i.test(right))
    ? (left > right ? 1 : -1)
    : (Number(left) > Number(right) ? 1 : -1))
);

const totalIsNegative = (totalAmount, transactionAmount, storeLocale) => (isNegativeAmount(transactionAmount) ? discountFormatter(totalAmount.replace(/[+/*-]/g, ''), storeLocale) : totalAmount);

const unPadStoreNumber = (sN) => ((/[A-Z]+/).test(sN) ? sN : `${Number(sN)}`);

const formatPercentage = (decimal) => parseFloat((decimal * 100).toFixed(2));

const cardNumberFormatter = (tenderInfo, redacted, isAdmin, storeLocale) => {
  const cardEntryType = cardEntryMethod(tenderInfo);
  if (!isAdmin) { return `${redacted}${cardEntryType}`; }
  if (storeLocale === 'ko-KR') return `${(tenderInfo?.Tender?.CreditDebit?.IssuerIdentificationNumber?.toString()?.padEnd(TRUNCATED_ACCOUNT_LENGTH_KOREA, 'X') || '')}${cardEntryType}`;
  return `${(tenderInfo?.Tender?.CreditDebit?.PrimaryAccountNumberLastFourDigits?.padStart(TRUNCATED_ACCOUNT_LENGTH, 'X') || '')}${cardEntryType}`;
};

const maskAmount = (amount) => {
  const decimalDeperator = amount?.toString()?.indexOf('0,') > -1 ? ',' : '.';
  const formatted = amount ? amount?.toString()?.replace(/[^0-9.,-]+/g, '') : '';
  return formatted ? formatted?.toString()?.replace(`0${decimalDeperator}`, `*${decimalDeperator}`) : '';
};

const translationReplaceNumber = (str, num) => (str ? str.replace('{x}', num || 0) : '');

const getCurrencySymbol = (currencyCode) => {
  if (overrideCurrencySymbols[currencyCode]) return overrideCurrencySymbols[currencyCode];

  //* Get the the symbol using the library */
  const symbol = getSymbolFromCurrency(currencyCode);
  return symbol || '';
};

const replaceAmountSymbols = (input, symbol) => {
  const regex = /^[^\d,\s]+|[^\d,\s]+$/g;
  return input.replace(regex, symbol);
};

const getLocalToForeignCurrency = (currency, formatted) => {
  const symbol = getCurrencySymbol(currency);
  return replaceAmountSymbols(formatted, symbol);
};

const getFormattedForeignCurrency = (foreignCurrency, formatLocale) => {
  const amount = foreignCurrency.OriginalFaceAmount?.ForeignAmount || foreignCurrency.OriginalFaceAmount?.Amount;

  if (!formatLocale) return null;
  // First format it into Store Locale then replace the symbol with foreign currency symbol
  const formatted = currencyFormatter(
      formatLocale,
      foreignCurrency.OriginalFaceAmount?.Currency,
      amount
    );
  return getLocalToForeignCurrency(foreignCurrency.OriginalFaceAmount?.Currency, formatted);
};

module.exports = {
  cardNumberFormatter,
  currencyFormatter,
  currencyFormatterException,
  currencyNumberFormatter,
  dateFormatter,
  dateFormatterTable,
  discountFormatter,
  extractAmountFilters,
  extractBooleanFilters,
  extractCategoryFilters,
  extractDateFilters,
  extractFreeTextFilter,
  extractModifierFilters,
  extractReturnTransactionFilter,
  extractSearchFilters,
  extractStatusFilters,
  extractTenderTypeAndSubFilters,
  extractTrainingModeFilter,
  formatFilters,
  formatFreeText,
  formatPercentage,
  formatSearchQueries,
  getFormattedForeignCurrency,
  getPromoText,
  maskAmount,
  searchDateFilter,
  sortStoresByNumber,
  totalIsNegative,
  translationReplaceNumber,
  unPadStoreNumber
};
